import { useCallback, useEffect, useMemo, useState } from "react";
import { useImagineChat } from "@/providers/imagine-chat-provider";
import { imagineStore } from "@/stores/imagine.store";

type UsePreviewHistoryOptions = {
  debug?: boolean;
};

export function usePreviewHistory(
  initialUrl: URL,
  options?: UsePreviewHistoryOptions,
) {
  const sandboxClient = imagineStore.use.sandboxState.sandboxClient();
  const debug = options?.debug ?? false;
  const [entries, setEntries] = useState<Array<URL>>([initialUrl]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const log = useCallback(
    (message: string, extra?: unknown) => {
      if (!debug) return;
      console.debug(`[preview-history] ${message}`, extra ?? "");
    },
    [debug],
  );

  const current = useMemo(
    () => entries[currentIndex] ?? initialUrl,
    [entries, currentIndex, initialUrl],
  );

  type HistoryMethod = "pushState" | "popState" | "replaceState";

  const handleUrlChange = useCallback(
    (url: URL, method?: string | boolean) => {
      const toHistoryMethod = (m?: string | boolean): HistoryMethod => {
        if (typeof m === "boolean") return m ? "popState" : "pushState";
        const low = (m || "pushState").toLowerCase();
        if (low === "popstate") return "popState";
        if (low === "replacestate") return "replaceState";
        return "pushState";
      };
      const normalized: HistoryMethod = toHistoryMethod(method);

      const urlString = url.toString();

      setEntries((prev) => {
        const next = [...prev];

        if (normalized === "pushState") {
          // Drop forward history
          const base = next.slice(0, currentIndex + 1);
          if (base[base.length - 1]?.toString() === urlString) {
            log("pushState ignored (same as last)", { url: urlString });
            return next;
          }
          const updated = [...base, url];
          setCurrentIndex(updated.length - 1);
          log("pushState", { toIndex: updated.length - 1, url: urlString });
          return updated;
        }

        if (normalized === "replaceState") {
          if (next[currentIndex]?.toString() !== urlString) {
            next[currentIndex] = url;
            log("replaceState", { index: currentIndex, url: urlString });
          }
          return next;
        }

        // popState: move index to existing URL if found, otherwise replace current
        const found = next.findIndex((u) => u.toString() === urlString);
        if (found >= 0) {
          if (found !== currentIndex) {
            setCurrentIndex(found);
            log("popState -> existing", { toIndex: found, url: urlString });
          }
          return next;
        }
        // Not found: replace current to keep in sync
        next[currentIndex] = url;
        log("popState -> replace current (not found)", {
          index: currentIndex,
          url: urlString,
        });
        return next;
      });
    },
    [currentIndex, log],
  );

  const canGoBack = currentIndex > 0;
  const canGoForward = currentIndex < entries.length - 1;
  const goBack = () => {
    sandboxClient?.historyBack();
  };
  const goForward = () => {
    sandboxClient?.historyForward();
  };

  // Reset history when initialUrl changes (compare by string value, not object reference)
  const initialUrlString = initialUrl.toString();
  useEffect(() => {
    setEntries([initialUrl]);
    setCurrentIndex(0);
  }, [initialUrlString]);

  useEffect(() => {
    if (!sandboxClient) return;

    const unsubscribe = sandboxClient.on("URL_CHANGED", (message) => {
      const { url, method } = message as { url?: string; method?: string };
      if (!url) return;

      try {
        const newUrl = new URL(String(url));
        handleUrlChange(newUrl, method ?? undefined);
      } catch (error) {
        log("URL_CHANGED handler failed", { error, raw: message });
      }
    });

    return () => {
      unsubscribe();
    };
  }, [sandboxClient, handleUrlChange, log]);

  return {
    // state
    history: entries,
    index: currentIndex,
    current,
    canGoBack,
    canGoForward,
    goBack,
    goForward,
    // alias for existing callers
    handleUrlChange,
  };
}
